// FRONTEND – src/routes/AppRoutes.js

import { memo, lazy, Suspense } from 'react';
import { Routes, Route, Outlet, Navigate } from 'react-router-dom';
import { Center, Spinner } from '@chakra-ui/react';

// Layout y guard (ligeros, se importan estáticos)
import { LayoutMenu } from '../components/general/LayoutMenu';
import { PrivateRoute as AuthGuard, RoleRouteGuard } from './authGuard/auth.guards';

// ---- Cargas diferidas (named exports → wrap a default) ----
const Login = lazy(() =>
  import('../components/Login').then(m => ({ default: m.Login }))
);

const LogoutPage = lazy(() => import('../pages/Logout'));

const Home = lazy(() =>
  import('../pages/Home').then(m => ({ default: m.Home }))
);

const Graphs = lazy(() =>
  import('../pages/Graphs').then(m => ({ default: m.default }))
);

const ViewAllCorporations = lazy(() =>
  import('../components/corporations/ViewAllCorporations').then(m => ({ default: m.ViewAllCorporations }))
);

const CoverageMapPage = lazy(() => import('../pages/CoverageMapPage'));

const ViewAllCenters = lazy(() =>
  import('../components/centers/ViewAllCenters').then(m => ({ default: m.ViewAllCenters }))
);

const ViewAllUsers = lazy(() =>
  import('../components/users/ViewAllUsers').then(m => ({ default: m.ViewAllUsers }))
);

const ViewAllDevices = lazy(() =>
  import('../components/watch/ViewAllDevices').then(m => ({ default: m.ViewAllDevices }))
);

const ViewAllCameras = lazy(() =>
  import('../components/cameras/ViewAllCameras').then(m => ({ default: m.ViewAllCameras }))
);

const ViewAllEmergencyButtons = lazy(() =>
  import('../components/emergencyButton/ViewAllEmergencyButtons').then(m => ({ default: m.ViewAllEmergencyButtons }))
);

const EventsPage = lazy(() => import('../pages/EventsPage'));

const ViewAllPatients = lazy(() =>
  import('../components/patients/ViewAllPatients').then(m => ({ default: m.ViewAllPatients }))
);

const Meeting = lazy(() => import('../pages/Meeting'));

const PatientProfilePage = lazy(() => import('../pages/PatientProfilePage'));
const PatientFormPage = lazy(() => import('../pages/PatientFormPage'));

const PageError = lazy(() =>
  import('../components/general/PageError').then(m => ({ default: m.PageError }))
);

// Envoltura para privadas con menú
const PrivateShell = () => (
  <LayoutMenu>
    <Outlet />
  </LayoutMenu>
);

// eslint-disable-next-line react/display-name
const AppRoutes = memo(() => {
  return (
    <Suspense
      fallback={
        <Center minH="60vh">
          <Spinner thickness="3px" speed="0.65s" />
        </Center>
      }
    >
      <Routes>
        {/* Públicas */}
        <Route path="/" element={<Login />} />
        <Route path="/logout" element={<LogoutPage />} />

        {/* Privadas */}
        <Route element={<AuthGuard />}>
          <Route element={<PrivateShell />}>
            {/* Rutas Comunes Accesibles para Todo Usuario Autenticado */}
            <Route path="/home" element={<Home />} />
            <Route path="/graphs" element={<Graphs />} />
            <Route path="/meeting" element={<Meeting />} />

            {/* Rutas de Eventos y Telemetría Clínica (Excluidos SuperAdmin y Corporación por RGPD) */}
            <Route
              element={
                <RoleRouteGuard
                  allowedGlobalRoles={[
                    'administrador',
                    'medico',
                    'enfermera',
                    'fisio',
                    'cuidadora',
                    'auxiliar',
                    'limpiadora',
                    'familiar',
                  ]}
                  allowAdminBypass={false}
                  redirectTo="/home"
                />
              }
            >
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/smartwatch" element={<EventsPage defaultTab="smartwatch" />} />
              <Route path="/events/emergency-button" element={<EventsPage defaultTab="emergency-button" />} />
            </Route>

            {/* Rutas de Pacientes (Excluido SuperAdmin por RGPD / Separación de funciones clínicas) */}
            <Route
              element={
                <RoleRouteGuard
                  allowedGlobalRoles={[
                    'administrador',
                    'corporacion',
                    'medico',
                    'enfermera',
                    'fisio',
                    'cuidadora',
                    'auxiliar',
                    'limpiadora',
                    'familiar',
                  ]}
                  allowAdminBypass={false}
                  redirectTo="/home"
                />
              }
            >
              <Route path="/patients" element={<ViewAllPatients />} />
              <Route path="/patients/:patientId" element={<PatientProfilePage />} />

              {/* Rutas Clínicas de Alta/Edición de Pacientes */}
              <Route
                element={
                  <RoleRouteGuard
                    allowedGlobalRoles={['administrador', 'corporacion', 'medico', 'enfermera']}
                    requiredCenterRole="admin"
                    allowAdminBypass={false}
                    redirectTo="/patients"
                  />
                }
              >
                <Route path="/patients/new" element={<PatientFormPage />} />
                <Route path="/patients/:patientId/edit" element={<PatientFormPage />} />
              </Route>
            </Route>

            {/* Rutas de Gobernanza: Corporaciones y Cobertura (Exclusivo Superadmin / Owner) */}
            <Route element={<RoleRouteGuard allowedGlobalRoles={[]} />}>
              <Route path="/corporations" element={<ViewAllCorporations />} />
              <Route path="/coverage-map" element={<CoverageMapPage />} />
            </Route>

            {/* Rutas de Gobernanza: Centros (Exclusivo Corporación y Admin de Centro; Superadmin bloqueado) */}
            <Route
              element={
                <RoleRouteGuard
                  allowedGlobalRoles={['corporacion']}
                  requiredCenterRole="admin"
                  allowAdminBypass={false}
                  redirectTo="/corporations"
                />
              }
            >
              <Route path="/centers" element={<ViewAllCenters />} />
            </Route>

            {/* Rutas de Gestión de Usuarios (Superadmin, Corporación o Admin de Centro) */}
            <Route element={<RoleRouteGuard allowedGlobalRoles={['administrador', 'corporacion']} requiredCenterRole="admin" />}>
              <Route path="/users" element={<ViewAllUsers />} />
            </Route>

            {/* Rutas de Dispositivos (Superadmin, Corporación o Admin de Centro) */}
            <Route element={<RoleRouteGuard allowedGlobalRoles={['administrador', 'corporacion']} requiredCenterRole="admin" />}>
              <Route path="/devices" element={<ViewAllDevices />} />
              <Route path="/smartwatches" element={<Navigate to="/devices" replace />} />
              <Route path="/cameras" element={<Navigate to="/devices" replace />} />
              <Route path="/emergencybuttons" element={<Navigate to="/devices" replace />} />
            </Route>
          </Route>
        </Route>

        {/* Fallback */}
        <Route path="*" element={<PageError />} />
      </Routes>
    </Suspense>
  );
});

AppRoutes.displayName = 'AppRoutes';
export default AppRoutes;
